"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Heart, RotateCcw, Sparkles } from "lucide-react";
import type { Gift } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  type SurpriseWheelExperienceData,
} from "@/data/experienceDataSchema";

export type SurpriseWheelVariant = "default" | "spotify";

interface SurpriseWheelPalette {
  accent: string;
  accentRgb: string;
}

interface SurpriseWheelSectionProps {
  gift: Partial<Gift>;
  variant: SurpriseWheelVariant;
  palette?: SurpriseWheelPalette;
  className?: string;
}

interface WheelOption {
  short: string;
  full: string;
  // Índice da opção no array original (`customData.options`). Necessário
  // pra mapear o `riggedIndex` (que aponta no array original) pro índice
  // já filtrado/usado pela roleta.
  originalIndex?: number;
}

const SPIN_DURATION_MS = 3200;
const REDUCED_SPIN_MS = 500;

const GENERIC_OPTIONS: WheelOption[] = [
  { short: "Filme juntinhos", full: "Escolher um filme para assistir juntinhos" },
  { short: "Próximo encontro", full: "Planejar com carinho o próximo encontro" },
  { short: "Mensagem doce", full: "Mandar uma mensagem carinhosa agora" },
  { short: "Surpresa de hoje", full: "Preparar uma surpresinha simples hoje" },
  { short: "Abraço apertado", full: "Trocar um abraço bem demorado" },
  { short: "Sonho a dois", full: "Sonhar com a próxima aventura do casal" },
];

function buildOptions(gift: Partial<Gift>): WheelOption[] {
  const personalized: WheelOption[] = [];
  if ((gift.photos?.length ?? 0) > 0) {
    personalized.push({
      short: "Memória especial",
      full: "Reviver uma memória especial do casal",
    });
  }
  if (gift.loveLetter && gift.loveLetter.trim().length > 0) {
    personalized.push({
      short: "Nossa carta",
      full: "Reler a nossa carta de amor com calma",
    });
  }
  if (gift.coupleStartDate && gift.coupleStartDate.trim().length > 0) {
    personalized.push({
      short: "Tempo juntos",
      full: "Celebrar o tempo que já vivemos juntos",
    });
  }

  const merged: WheelOption[] = [...personalized];
  for (const generic of GENERIC_OPTIONS) {
    if (merged.length >= 6) break;
    merged.push(generic);
  }
  return merged.slice(0, 6);
}

// Reduz uma opção customizada a um "short" curto para caber na fatia da
// roleta sem quebrar visualmente. Mantemos o "full" como o texto completo.
function buildShortLabel(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) return "Surpresa";
  if (trimmed.length <= 18) return trimmed;
  return trimmed.slice(0, 16).trimEnd() + "…";
}

function buildCustomOptions(
  options: SurpriseWheelExperienceData["options"]
): WheelOption[] | null {
  if (!Array.isArray(options) || options.length === 0) return null;
  const result: WheelOption[] = [];
  for (let i = 0; i < options.length; i++) {
    const raw = options[i];
    if (typeof raw !== "string") continue;
    const full = raw.trim();
    if (!full) continue;
    result.push({ short: buildShortLabel(full), full, originalIndex: i });
    if (result.length >= 8) break;
  }
  return result.length >= 2 ? result : null;
}

function pickRandomIndex(count: number, exclude?: number | null): number {
  if (count <= 1) return 0;
  let idx = Math.floor(Math.random() * count);
  if (exclude !== undefined && exclude !== null && idx === exclude) {
    idx = (idx + 1) % count;
  }
  return idx;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

export function SurpriseWheelSection({
  gift,
  variant,
  palette,
  className,
}: SurpriseWheelSectionProps) {
  const customData = useMemo(
    () => getExperienceData<SurpriseWheelExperienceData>(gift, "surprise-wheel"),
    [gift]
  );
  const options = useMemo(() => {
    const custom = buildCustomOptions(customData?.options);
    if (custom) return custom;
    return buildOptions(gift);
  }, [gift, customData]);
  const title = customData?.title?.trim();
  const question = customData?.question?.trim();
  const sliceAngle = 360 / Math.max(1, options.length);

  // Resolve o índice "fixo" definido no editor (apontando no array original
  // de options) para o índice correspondente na lista de roleta já filtrada.
  // Se a opção rigged foi removida/ficou vazia, cai pro aleatório.
  const forcedIndex = useMemo<number | null>(() => {
    const rigged = customData?.riggedIndex;
    if (typeof rigged !== "number" || rigged < 0) return null;
    const idx = options.findIndex((o) => o.originalIndex === rigged);
    return idx >= 0 ? idx : null;
  }, [customData?.riggedIndex, options]);

  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [resultIndex, setResultIndex] = useState<number | null>(null);
  const lastResultRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const spin = () => {
    if (spinning || options.length === 0) return;
    const targetIdx =
      forcedIndex !== null
        ? forcedIndex
        : pickRandomIndex(options.length, lastResultRef.current);
    lastResultRef.current = targetIdx;

    const targetModRaw = -((targetIdx + 0.5) * sliceAngle);
    const targetMod = ((targetModRaw % 360) + 360) % 360;
    const currentMod = ((rotation % 360) + 360) % 360;
    const deltaToTarget = (targetMod - currentMod + 360) % 360;
    const turns = reducedMotion ? 0 : 5;
    const duration = reducedMotion ? REDUCED_SPIN_MS : SPIN_DURATION_MS;
    const newRotation = rotation + turns * 360 + deltaToTarget;

    setSpinning(true);
    setResultIndex(null);
    setRotation(newRotation);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setSpinning(false);
      setResultIndex(targetIdx);
    }, duration);
  };

  const animationDuration = reducedMotion ? REDUCED_SPIN_MS : SPIN_DURATION_MS;

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        options={options}
        rotation={rotation}
        spinning={spinning}
        resultIndex={resultIndex}
        animationDuration={animationDuration}
        onSpin={spin}
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        className={className}
        title={title}
        question={question}
      />
    );
  }

  return (
    <DefaultVariant
      options={options}
      rotation={rotation}
      spinning={spinning}
      resultIndex={resultIndex}
      animationDuration={animationDuration}
      onSpin={spin}
      className={className}
      title={title}
      question={question}
    />
  );
}

// ─── Variantes ───────────────────────────────────────────────────────────

interface VariantInnerProps {
  options: WheelOption[];
  rotation: number;
  spinning: boolean;
  resultIndex: number | null;
  animationDuration: number;
  onSpin: () => void;
  className?: string;
  title?: string;
  question?: string;
}

const DEFAULT_PALETTE_COLORS = [
  "#E11D48",
  "#F59E0B",
  "#BE185D",
  "#EC4899",
  "#FB7185",
  "#9D174D",
];

function DefaultVariant({
  options,
  rotation,
  spinning,
  resultIndex,
  animationDuration,
  onSpin,
  className,
  title,
  question,
}: VariantInnerProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "surprise-wheel-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <DecorativeAccents variant="default" />

      <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Sparkles className="h-3 w-3 text-[#E11D48]" />
            Roleta surpresa
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "Uma surpresinha para nós"}
          </h2>
          <p className="text-sm text-[#881337]/80 max-w-md">
            {question ||
              "Gire e deixe a roleta escolher um gesto carinhoso para o casal hoje."}
          </p>
        </header>

        <div className="flex flex-col items-center gap-6">
          <Wheel
            options={options}
            rotation={rotation}
            spinning={spinning}
            animationDuration={animationDuration}
            colors={DEFAULT_PALETTE_COLORS}
            variant="default"
            sizeClamp="clamp(220px, 78vw, 300px)"
          />
          <ResultPanel
            options={options}
            resultIndex={resultIndex}
            spinning={spinning}
            onSpin={onSpin}
            variant="default"
          />
        </div>
      </div>
    </motion.section>
  );
}

interface SpotifyVariantProps extends VariantInnerProps {
  palette: SurpriseWheelPalette;
}

function SpotifyVariant({
  options,
  rotation,
  spinning,
  resultIndex,
  animationDuration,
  onSpin,
  palette,
  className,
  title,
  question,
}: SpotifyVariantProps) {
  const colors = useMemo(
    () => [
      palette.accent,
      `rgba(${palette.accentRgb}, 0.55)`,
      "#7C3AED",
      "#F59E0B",
      `rgba(${palette.accentRgb}, 0.85)`,
      "#9D174D",
    ],
    [palette.accent, palette.accentRgb]
  );

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "surprise-wheel-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <DecorativeAccents variant="spotify" accent={palette.accent} />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Roleta surpresa
          </span>
        </header>
        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "Uma surpresinha para nós"}
          </h3>
          <p className="text-[11px] text-white/55 tracking-wide">
            {question || "Gire e descubra um gesto carinhoso para o casal hoje."}
          </p>
        </div>

        <div className="flex flex-col items-center gap-4">
          <Wheel
            options={options}
            rotation={rotation}
            spinning={spinning}
            animationDuration={animationDuration}
            colors={colors}
            variant="spotify"
            palette={palette}
            sizeClamp="clamp(190px, 68vw, 260px)"
          />
          <ResultPanel
            options={options}
            resultIndex={resultIndex}
            spinning={spinning}
            onSpin={onSpin}
            variant="spotify"
            palette={palette}
          />
        </div>
      </div>
    </motion.section>
  );
}

// ─── Roleta ──────────────────────────────────────────────────────────────

interface WheelProps {
  options: WheelOption[];
  rotation: number;
  spinning: boolean;
  animationDuration: number;
  colors: string[];
  variant: SurpriseWheelVariant;
  palette?: SurpriseWheelPalette;
  sizeClamp: string;
}

function Wheel({
  options,
  rotation,
  spinning,
  animationDuration,
  colors,
  variant,
  palette,
  sizeClamp,
}: WheelProps) {
  const n = Math.max(1, options.length);
  const sliceAngle = 360 / n;
  const isSpotify = variant === "spotify";

  const pointerColor = isSpotify ? palette?.accent ?? "#1DB954" : "#E11D48";

  const slicePath = (i: number): string => {
    const startAngle = (i * sliceAngle - 90) * (Math.PI / 180);
    const endAngle = ((i + 1) * sliceAngle - 90) * (Math.PI / 180);
    const r = 50;
    const cx = 50;
    const cy = 50;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = sliceAngle > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  return (
    <div
      className="relative shrink-0"
      style={{ width: sizeClamp, height: sizeClamp }}
    >
      {/* Ponteiro */}
      <div
        className="absolute left-1/2 z-30 pointer-events-none"
        style={{
          top: 0,
          transform: "translate(-50%, -42%)",
          width: 26,
          height: 30,
          background: pointerColor,
          clipPath: "polygon(50% 100%, 0 0, 100% 0)",
          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.30))",
        }}
        aria-hidden="true"
      />

      {/* Aro decorativo */}
      <div
        className="absolute inset-0 rounded-full p-[6px]"
        style={{
          background: isSpotify
            ? "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04))"
            : "linear-gradient(135deg, #FFF7ED, #FFE4E6)",
          boxShadow: isSpotify
            ? `0 16px 40px rgba(${palette?.accentRgb ?? "29,185,84"},0.28)`
            : "0 14px 32px rgba(225,29,72,0.20)",
        }}
      >
        <div
          className="relative w-full h-full rounded-full overflow-hidden"
          style={{ background: isSpotify ? "#0f0f12" : "#ffffff" }}
        >
          <div
            className="absolute inset-0"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning
                ? `transform ${animationDuration}ms cubic-bezier(0.16, 1, 0.3, 1)`
                : "none",
              willChange: "transform",
            }}
          >
            <svg
              viewBox="0 0 100 100"
              className="absolute inset-0 w-full h-full"
              role="img"
              aria-label={`Roleta com ${n} surpresas românticas`}
            >
              {options.map((_, i) => (
                <path
                  key={i}
                  d={slicePath(i)}
                  fill={colors[i % colors.length]}
                  stroke={isSpotify ? "rgba(0,0,0,0.35)" : "#ffffff"}
                  strokeWidth="0.6"
                />
              ))}
              <circle
                cx="50"
                cy="50"
                r="10.5"
                fill={isSpotify ? "#0f0f12" : "#ffffff"}
                stroke={pointerColor}
                strokeWidth="1.2"
              />
            </svg>

            {options.map((opt, i) => {
              const midAngle = (i + 0.5) * sliceAngle - 90;
              const rad = (midAngle * Math.PI) / 180;
              const radius = 33; // percentage from center
              const x = 50 + radius * Math.cos(rad);
              const y = 50 + radius * Math.sin(rad);
              const flipped = midAngle > 90 || midAngle < -90;
              const labelRot = flipped ? midAngle - 180 : midAngle;
              return (
                <div
                  key={i}
                  className="absolute font-bold leading-none tracking-tight text-white"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: `translate(-50%, -50%) rotate(${labelRot}deg)`,
                    fontSize: "clamp(10px, 2.6vw, 12px)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.55)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                  }}
                >
                  {opt.short}
                </div>
              );
            })}
          </div>

          {/* Centro fixo */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Heart
              className="h-5 w-5"
              style={{ color: pointerColor }}
              fill="currentColor"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Painel do resultado ─────────────────────────────────────────────────

interface ResultPanelProps {
  options: WheelOption[];
  resultIndex: number | null;
  spinning: boolean;
  onSpin: () => void;
  variant: SurpriseWheelVariant;
  palette?: SurpriseWheelPalette;
}

function ResultPanel({
  options,
  resultIndex,
  spinning,
  onSpin,
  variant,
  palette,
}: ResultPanelProps) {
  const isSpotify = variant === "spotify";
  const result =
    resultIndex !== null && resultIndex >= 0 && resultIndex < options.length
      ? options[resultIndex]
      : null;

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-sm">
      <button
        type="button"
        onClick={onSpin}
        disabled={spinning}
        aria-label={
          spinning
            ? "Roleta girando, aguarde"
            : result
              ? "Girar a roleta novamente"
              : "Girar a roleta surpresa"
        }
        className={cn(
          "relative inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-[0.16em] transition-all duration-300 active:scale-95",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          spinning
            ? "cursor-wait opacity-80"
            : "cursor-pointer hover:-translate-y-0.5"
        )}
        style={
          isSpotify
            ? {
                background: palette?.accent ?? "#1DB954",
                color: "#0F0F12",
                boxShadow: `0 12px 32px rgba(${palette?.accentRgb ?? "29,185,84"},0.35)`,
              }
            : {
                background:
                  "linear-gradient(135deg, #E11D48 0%, #881337 100%)",
                color: "#ffffff",
                boxShadow: "0 12px 28px rgba(225,29,72,0.30)",
              }
        }
      >
        {result && !spinning ? (
          <RotateCcw className="h-4 w-4" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        <span>
          {spinning ? "Girando..." : result ? "Girar de novo" : "Girar roleta"}
        </span>
      </button>

      <motion.div
        key={resultIndex !== null ? `r-${resultIndex}` : "empty"}
        initial={{ opacity: 0, y: 8, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
        aria-live="polite"
        aria-atomic="true"
        className="w-full rounded-2xl px-5 py-4 text-center"
        style={
          isSpotify
            ? {
                background: `rgba(${palette?.accentRgb ?? "29,185,84"},0.10)`,
                border: `1px solid rgba(${palette?.accentRgb ?? "29,185,84"},0.22)`,
              }
            : {
                background: "rgba(255,255,255,0.78)",
                border: "1px solid rgba(225,29,72,0.18)",
                boxShadow: "0 10px 24px rgba(225,29,72,0.10)",
              }
        }
      >
        {result ? (
          <>
            <p
              className="text-[10px] font-bold uppercase tracking-[0.18em]"
              style={
                isSpotify
                  ? { color: palette?.accent ?? "#1DB954" }
                  : { color: "#881337" }
              }
            >
              A roleta escolheu
            </p>
            <p
              className={cn(
                "mt-2 text-lg sm:text-xl font-semibold leading-snug",
                isSpotify ? "text-white" : "text-[#1F2937]"
              )}
              style={
                !isSpotify
                  ? { fontFamily: "var(--font-serif, serif)" }
                  : undefined
              }
            >
              {result.full}
            </p>
            <p
              className={cn(
                "mt-2 text-xs",
                isSpotify ? "text-white/55" : "text-[#1F2937]/65"
              )}
            >
              Um pequeno gesto pode transformar o dia em um momento inesquecível.
            </p>
          </>
        ) : (
          <p
            className={cn(
              "text-sm",
              isSpotify ? "text-white/70" : "text-[#1F2937]/75"
            )}
          >
            {spinning
              ? "A surpresa está chegando..."
              : "Toque em girar e descubra uma surpresinha romântica para hoje."}
          </p>
        )}
      </motion.div>
    </div>
  );
}

// ─── Decoração ───────────────────────────────────────────────────────────

function DecorativeAccents({
  variant,
  accent,
}: {
  variant: SurpriseWheelVariant;
  accent?: string;
}) {
  if (variant === "spotify") {
    return (
      <>
        <Heart
          className="absolute -top-2 -right-2 h-14 w-14 opacity-15"
          style={{ color: accent ?? "#1DB954" }}
          fill="currentColor"
        />
        <Sparkles
          className="absolute bottom-4 -left-2 h-9 w-9 opacity-15"
          style={{ color: accent ?? "#1DB954" }}
        />
      </>
    );
  }
  return (
    <>
      <Heart
        className="absolute -top-5 -right-4 h-24 w-24 text-[#E11D48]/15"
        fill="currentColor"
      />
      <Sparkles className="absolute top-4 left-4 h-5 w-5 text-[#F59E0B]/45" />
      <Heart
        className="absolute -bottom-4 -left-3 h-14 w-14 text-[#F59E0B]/25"
        fill="currentColor"
      />
    </>
  );
}
